import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "@/i18n/LanguageContext";
import Index from "./pages/Index";
const Configurator = lazy(() => import("./pages/Configurator"));
const Rental = lazy(() => import("./pages/Rental"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const WasteClassifier = lazy(() => import("./pages/WasteClassifier"));
import { CookieConsent, initGoogleConsentMode, loadGoogleAnalytics } from "./components/CookieConsent";

// Initialize Google Consent Mode v2 immediately (before GA loads)
initGoogleConsentMode();
// Load GA script - Consent Mode controls what data is collected
loadGoogleAnalytics();

const queryClient = new QueryClient();

// Wrapper component that provides language context
const LocalizedRoutes = ({ lang }: { lang: "it" | "en" }) => {
  return (
    <LanguageProvider defaultLang={lang}>
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/configurator" element={<Configurator />} />
          <Route path="/rental" element={<Rental />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/waste-classifier" element={<WasteClassifier />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <CookieConsent />
    </LanguageProvider>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
          {/* Redirect root to /it */}
          <Route path="/" element={<Navigate to="/it" replace />} />
          
          {/* Language-specific routes */}
          <Route path="/it/*" element={<LocalizedRoutes lang="it" />} />
          <Route path="/en/*" element={<LocalizedRoutes lang="en" />} />
          
          {/* Fallback for old routes without language prefix */}
          <Route path="/privacy-policy" element={<Navigate to="/it/privacy-policy" replace />} />
          <Route path="/cookie-policy" element={<Navigate to="/it/cookie-policy" replace />} />
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/it" replace />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
